import { useEffect, useState } from 'react';
import { useSwrTick } from '@/lib/swrRefresh';
import axios from 'axios';
import { History, AlertCircle, TrendingUp, TrendingDown } from 'lucide-react';
import { usePortfolioScopeParams } from '@/lib/portfolioScopeParams';
import { dedupedGet } from '@/lib/dedupedGet';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * HistoricalEarningsCard — Round 77b (Feb 12, 2026)
 *
 * Companion to the past-month calendar fidelity fix. Asks the new
 * `/api/portfolio/historical-earnings` endpoint how much the user's
 * CURRENT positions WOULD have earned in dividends across each of
 * the last 4 calendar years, using REAL historical dividend
 * payments (cached `dividend_history_24m`) × today's share count.
 *
 * Honesty rails:
 *   * Hard disclaimer above the chart: "Hypothetical — not what you
 *     actually received."
 *   * Per-year coverage badge ("full" / "partial" / "none") so users
 *     can tell which years have a credible number behind them.
 *   * Years older than 24 months (the cache window) typically come
 *     back `coverage: 'none'`. We render those as a stub with an
 *     "(no cached history)" footnote — never as a $0 misleading bar.
 */
export default function HistoricalEarningsCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const scopeParams = usePortfolioScopeParams();
  const scopeKey = scopeParams.portfolio_id || 'all';

  const swrTick = useSwrTick('/portfolio/summary');
  useEffect(() => {
    // Round 78b — read historical earnings from the same
    // /portfolio/summary endpoint that the rest of the dashboard
    // already hits on login / page load. No separate request for
    // this card; we're piggy-backing on the eager portfolio fetch
    // so the data is hydrated by the time the Calendar tab opens.
    // Browsers + the FastAPI server happily dedupe repeated GETs.
    setLoading(true);
    dedupedGet(`${API}/portfolio/summary`, { params: scopeParams, withCredentials: true })
      .then(res => setData(res.data?.historical_earnings || null))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [scopeKey, swrTick]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return null;
  if (!data || !Array.isArray(data.years) || data.years.length === 0) return null;

  const years = data.years;
  // Find the max for bar-height scaling so the chart fills the
  // available height regardless of portfolio size.
  const maxEarnings = Math.max(...years.map(y => y.total_earnings || 0), 1);
  const allZero = years.every(y => (y.total_earnings || 0) === 0);
  if (allZero) {
    // Brand-new portfolios or full cache miss — stay quiet rather
    // than show a row of empty bars.
    return null;
  }

  return (
    <div
      className="rounded-2xl border bg-card p-4 sm:p-5"
      data-testid="historical-earnings-card"
    >
      <div className="flex items-start sm:items-center justify-between gap-2 mb-3 flex-wrap">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-lg bg-amber-100 dark:bg-amber-950/40 flex items-center justify-center">
            <History className="w-4 h-4 text-amber-600 dark:text-amber-400" />
          </div>
          <div>
            <h3 className="text-sm font-bold tracking-tight">Historical Earnings (Hypothetical)</h3>
            <p className="text-[11px] text-muted-foreground">
              What your CURRENT positions would have earned in real dividends each year.
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
        {years.map(y => {
          const v = y.total_earnings || 0;
          const cov = y.coverage || 'none';
          const tone =
            cov === 'full' ? 'border-emerald-300/60 dark:border-emerald-700/60'
            : cov === 'partial' ? 'border-amber-300/60 dark:border-amber-700/60'
            : 'border-muted-foreground/20 border-dashed';
          const valueTone =
            cov === 'full' ? 'text-emerald-700 dark:text-emerald-400'
            : cov === 'partial' ? 'text-amber-700 dark:text-amber-400'
            : 'text-muted-foreground/70';
          const barPct = maxEarnings > 0 ? Math.min(100, (v / maxEarnings) * 100) : 0;
          const growth = y.growth_pct_vs_prev;
          return (
            <div
              key={y.year}
              className={`rounded-lg border bg-muted/10 p-3 ${tone}`}
              data-testid={`hist-earn-${y.year}`}
              title={
                cov === 'full'
                  ? `${y.year} — every position in your portfolio had cached dividend history for this year. Number is reliable within ±1% (rounding).`
                  : cov === 'partial'
                    ? `${y.year} — partial coverage. ${y.tickers_missing_history?.length || 0} position(s) had no cached history for this year and contributed $0. Real total was higher.`
                    : `${y.year} — no cached history available (older than the 24-month window).`
              }
            >
              <div className="flex items-baseline justify-between gap-1 mb-1">
                <span className="text-xs text-muted-foreground font-semibold">{y.year}</span>
                {growth !== null && growth !== undefined && (
                  <span
                    className={`inline-flex items-center gap-0.5 text-[10px] font-mono ${
                      growth >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'
                    }`}
                  >
                    {growth >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                    {growth >= 0 ? '+' : ''}{growth.toFixed(1)}%
                  </span>
                )}
              </div>
              <div className={`text-base font-bold font-mono ${valueTone}`}>
                ${v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
              {/* Bar chart */}
              <div className="mt-1.5 h-1.5 rounded-full bg-muted/40 overflow-hidden">
                <div
                  className={`h-full ${
                    cov === 'full' ? 'bg-emerald-500/70'
                    : cov === 'partial' ? 'bg-amber-500/70'
                    : 'bg-muted-foreground/30'
                  }`}
                  style={{ width: `${barPct}%` }}
                />
              </div>
              <p className="text-[10px] text-muted-foreground mt-1.5">
                {cov === 'full' && 'full coverage'}
                {cov === 'partial' && `${y.tickers_missing_history?.length || 0} ticker(s) missing`}
                {cov === 'none' && 'no cached history'}
              </p>
            </div>
          );
        })}
      </div>

      <div className="mt-3 flex items-start gap-1.5 text-[10px] text-muted-foreground">
        <AlertCircle className="w-3 h-3 mt-0.5 shrink-0" />
        <p>
          {data.disclaimer || 'Hypothetical figures.'} Data window: last 24 months — older years
          may show no coverage.
          {data.tickers_with_no_history?.length > 0 && (
            <>
              {' '}
              <span className="text-amber-700 dark:text-amber-400 font-semibold">
                {data.tickers_with_no_history.length} ticker(s) in your portfolio have no cached
                history yet — they'll fill in after the next refresh.
              </span>
            </>
          )}
        </p>
      </div>
    </div>
  );
}
